import * as zmq from 'zeromq';
import { createPrivateKey, createPublicKey } from 'crypto';
import { z85Decode, z85Encode } from './z85.js';

/** A server's CurveZMQ identity, both halves Z85-encoded (40 characters each). */
export type ServerKeyPair = { publicKey: string; secretKey: string };

/** Fresh CurveZMQ keypair (libzmq's `zmq_curve_keypair`). */
export function generateServerKeyPair(): ServerKeyPair {
  const { publicKey, secretKey } = zmq.curveKeyPair();
  return { publicKey, secretKey };
}

/** True for a well-formed Z85 CurveZMQ key (40 chars → 32 bytes). */
export function isCurveKey(value: unknown): value is string {
  if (typeof value !== 'string' || value.length !== 40) return false;
  try {
    return z85Decode(value).length === 32;
  } catch {
    return false;
  }
}

// RFC 8410 PKCS#8 prefix for a raw 32-byte X25519 private key. CurveZMQ keys are
// plain curve25519 scalars/points, so node's X25519 derives the same public key
// libsodium's crypto_scalarmult_base does — which is what lets a keys file be
// checked for a secret key that does not belong to its public key. Kept as hex and
// decoded per call: @gotti/server is bundled into the browser playground, where a
// module-level Buffer would throw at load (there is no Buffer global there).
const X25519_PKCS8_PREFIX_HEX = '302e020100300506032b656e04220420';

/** The public key that belongs to `secretKey` (both Z85). */
export function derivePublicKey(secretKey: string): string {
  const raw = z85Decode(secretKey);
  const priv = createPrivateKey({ key: Buffer.concat([Buffer.from(X25519_PKCS8_PREFIX_HEX, 'hex'), raw]), format: 'der', type: 'pkcs8' });
  const spki = createPublicKey(priv).export({ format: 'der', type: 'spki' }) as Buffer;
  return z85Encode(spki.subarray(spki.length - 32));
}

/**
 * Validate a configured identity, naming the server in every failure. Returns
 * the pair when both halves are present and belong together.
 */
export function assertServerKeyPair(serverId: string | number, pair: { publicKey?: unknown; secretKey?: unknown }): ServerKeyPair {
  const { publicKey, secretKey } = pair;
  if (!isCurveKey(publicKey)) {
    throw new Error(`Server '${serverId}': publicKey is not a 40-character Z85 CurveZMQ key.`);
  }
  if (!isCurveKey(secretKey)) {
    throw new Error(`Server '${serverId}': secretKey is not a 40-character Z85 CurveZMQ key.`);
  }
  if (derivePublicKey(secretKey) !== publicKey) {
    throw new Error(`Server '${serverId}': secretKey does not belong to publicKey — the keys file entry is corrupt or mixed up.`);
  }
  return { publicKey, secretKey };
}
